import { injectable } from 'inversify';

import { Calendar } from '@vk-hr-tek/core/calendar';

import {
  ContentSurveyItem,
  ContentSurveyQuestion,
  ContentSurveyUserAnswer,
} from '@app/gen/community';

import { ContentSurveysPassingDto } from '../dto';
import {
  ContentSurveysPassingAnswer,
  TextStatisticsTypes,
  TextWithFetchStatisticsTypes,
} from '../types';

export type ProcessedSurveyPassingItem = ReturnType<
  ContentSurveysPassingMapper['processSurveyPassing']
>;

@injectable()
export class ContentSurveysPassingMapper {
  constructor(private calendar: Calendar) {}

  public processSurveyPassing(surveyItem: ContentSurveyItem) {
    const { survey, passing, actions } = surveyItem;

    return {
      id: survey.id,
      title: survey.title,
      anonymous: survey.anonymous,
      showResult: survey.show_result,
      isPassing: !!passing?.answers?.length,
      completedAt: survey.completed_at,
      isCompleted: survey.completed_at
        ? new Date(survey.completed_at) < new Date()
        : false,
      completedLabel: this.getCompletedLabel(survey.completed_at),
      questions: survey.questions,
      passesNum: surveyItem.passes_num,
      actions,
      passing,
      version: survey.version,
    };
  }

  private getCompletedLabel(dateString?: string) {
    if (!dateString) return 'Срок не ограничен';

    const date = new Date(dateString);
    const isSurveyExpired = dateString
      ? new Date(dateString) < new Date()
      : false;

    if (!this.calendar.isValidDate(date)) return dateString;
    if (isSurveyExpired) return 'Истек период прохождения';

    const dateFormat = new Intl.DateTimeFormat('ru-RU', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
    }).format(date);

    const timeFormat = new Intl.DateTimeFormat('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);

    return `До ${dateFormat} ${timeFormat}`;
  }

  public processSurveyQuestions(
    questions?: ContentSurveyQuestion[],
  ): ContentSurveysPassingDto {
    return {
      questions: (questions || []).map((question) => {
        const { answer_settings: answerSettings } = question;

        const { answer_type: type } = answerSettings.object;

        const getAnswerValues = (): ContentSurveysPassingAnswer => {
          switch (type) {
            case 'free_text':
            case 'single_text_choice':
              return { type };

            case 'multiple_text_choice': {
              const { answer_options: answerOptions } = answerSettings.object;

              return {
                type,
                answer: answerOptions.map((option) => ({
                  id: option?.id,
                  checked: false,
                })),
              };
            }
          }
        };

        return {
          ...question,
          ...getAnswerValues(),
        };
      }),
    };
  }

  public processSurveyAnswers(
    values: ContentSurveysPassingDto,
  ): ContentSurveyUserAnswer[] {
    const result: ContentSurveyUserAnswer[] = [];

    values.questions.forEach((question) => {
      const { id, type, answer } = question;
      const isEmptyAnswer =
        !answer ||
        (Array.isArray(answer) && answer.every((item) => !item.checked));

      if (isEmptyAnswer) {
        return;
      }

      const getAnswerValue = (): ContentSurveyUserAnswer['user_answer'] => {
        switch (type) {
          case 'free_text':
            return { object: { answer_type: type, text: answer } };
          case 'single_text_choice':
            return {
              object: {
                answer_type: type,
                selected_option: answer,
              },
            };
          case 'multiple_text_choice': {
            const selectedOptions: string[] = [];

            answer.forEach((answerItem) => {
              if (answerItem.id && answerItem.checked) {
                selectedOptions.push(answerItem.id);
              }
            });

            return {
              object: {
                answer_type: type,
                selected_options: selectedOptions,
              },
            };
          }
        }
      };

      if (id) {
        result.push({ question_id: id, user_answer: getAnswerValue() });
      }
    });

    return result;
  }

  public processAnalyticsStatisticsAnswer(
    statistics: TextStatisticsTypes | TextWithFetchStatisticsTypes,
  ) {
    const answerType = statistics.answer_type;

    switch (answerType) {
      case 'multiple_choice':
        return {
          answerType: statistics.answer_type,
          totalAnswers: statistics.total_answers,
          options: statistics.options,
          otherValueStats: statistics.other_value_stats,
        };
      case 'single_choice':
        return {
          answerType: statistics.answer_type,
          totalAnswers: statistics.total_answers,
          options: statistics.options,
          otherValueStats: statistics.other_value_stats,
        };
      case 'free_text':
        return {
          answerType: statistics.answer_type,
          totalAnswers: statistics.total_answers,
          userAnswer: statistics.user_answer,
        };
    }
  }
}
